import { useTranslation } from 'react-i18next';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker';
import { DateRangePicker } from '@unified-checkout/design-system';

import type { IDateRangesProps } from '@app/dashboard/types/innerLayout/date-ranges';
import { customLocales, getPickerBtnText, mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';

const DateRanges = ({
  appliedRanges,
  handleRangesChange,
  customPreDefinedRanges,
  containerRef,
  datePickerLabel,
  lang,
  disabled = false,
  resetable = true,
  disabledApplyBtn = false,
  onClear,
  onChange,
  customLocale,
  jumpToStartDateOnShortcutClick = true,
  shouldSelectOneDate = false,
  shouldHideBtnText = false,
  shouldApplyDefaultRange = true,
  shouldHideBtnRangeName = false,
  ...rest
}: IDateRangesProps) => {
  const { t } = useTranslation();
  const [shouldDisableApplyBtn, setShouldDisableApplyBtn] = useState(false);
  
  const preDefinedRanges: IPreDefinedRange[] = useMemo(
    () => customPreDefinedRanges || mainPreDefinedRanges(t),
    [customPreDefinedRanges, t]
  );

  const defaultPreDefinedRange: IPreDefinedRange | undefined = useMemo(
    () => preDefinedRanges.find((range: IPreDefinedRange) => range.default),
    [preDefinedRanges]
  );

  const {
    selectionRange: defaultSelectionRange,
    index: defaultIndex,
    name: defaultName,
  } = defaultPreDefinedRange ?? {};

  const { start, end, index, name } = appliedRanges || {};

  const applyDefaultRange = useCallback(() => {
    handleRangesChange({
      index: defaultIndex,
      name: defaultName,
      start: defaultSelectionRange?.[0],
      end: defaultSelectionRange?.[1],
    });
  }, [defaultIndex, defaultName, defaultSelectionRange, handleRangesChange]);

  const preDefinedRangesWithSelection = useMemo(() => {
    return preDefinedRanges.map((range) => {
      const isSelected = range.index === index;

      return {
        ...range,
        ...(range.name === 'custom' ? { selectionRange: [start, end] as [Date, Date] } : {}),
        selected: isSelected,
      };
    });
  }, [preDefinedRanges, index, start, end]);  

  const onApplyHandler = (
    range?: [Date, Date],
    rangeIndex?: number,
    rangeName?: string
  ) => {
    const [startDate, endDate] = range as [Date, Date];
    handleRangesChange({
      index: rangeIndex,
      name: rangeName,
      start: startDate,
      end: endDate,
    });
  };

  const onClearDateHandler = () => {
    applyDefaultRange();
    onClear?.();
  }

  const onDateChangeHandler = (range?: [Date, Date]) => {
    setShouldDisableApplyBtn(!range);
    onChange?.(range);
  };

  const textDatePicker = useMemo(() => {
    const startDate = start;
    if (!startDate || shouldHideBtnText) return '';

    const endDate = end as Date;
    const rangeName = name && !shouldHideBtnRangeName ? name : '';

    return getPickerBtnText(t, start, endDate, rangeName, shouldSelectOneDate);
  }, [
    t,
    start,
    end,
    name,
    shouldSelectOneDate,
    shouldHideBtnText,
    shouldHideBtnRangeName
  ])

   useEffect(() => {
    if (shouldApplyDefaultRange) {
      applyDefaultRange();
    }
  }, [shouldApplyDefaultRange]);
  
  return (
    <DateRangePicker
      containerRef={containerRef as RefObject<HTMLDialogElement> | undefined}
      label={datePickerLabel}
      lang={lang}
      text={textDatePicker}
      onApply={onApplyHandler}
      onClear={onClearDateHandler}
      customLocale={customLocale || customLocales}
      preDefinedRanges={preDefinedRangesWithSelection}
      disabled={disabled}
      jumpToStartDateOnShortcutClick={jumpToStartDateOnShortcutClick}
      onChange={onDateChangeHandler}
      disabledApplyBtn={disabledApplyBtn || shouldDisableApplyBtn}
      resetable= {resetable}
      shouldSelectOneDate={shouldSelectOneDate}
      {...rest}
    />
  );
};

export default DateRanges;
